/**
 * ChatProductCards / ChatProductCardsDesign — the in-thread product cards.
 *
 * Two named exports from one file, deliberately not one component behind a
 * boolean prop (code review, Task 5 fix round, finding C1):
 *
 *   `ChatProductCards`        the untouched legacy carousel (`--g-*` tokens).
 *                              `MessageList.tsx` still imports this name, so
 *                              nothing moves on ANY viewport until a later
 *                              task opts a call site into the new one BY
 *                              NAME — exactly how `EstimateCheckCard` vs
 *                              `ChatMaterialsTable` is already picked.
 *   `ChatProductCardsDesign`  the design-world rebuild (spec §4.6, `--gd-*`
 *                              tokens) built in this task.
 *
 * WHY EXPORTS, NOT A PROP. `design-world.css` defines every `--gd-*` token on
 * `:root` only — there is no `[data-theme="dark"]` block — while
 * `ThemeController.tsx` writes `data-theme` app-wide from a shipped user
 * setting. A `--gd-*` component mounted on an ungated surface renders a
 * cream card with near-black text inside a dark thread, at every viewport,
 * not only below 1200px — `EstimateCheckCard` escapes this only because
 * Task 9 gates it, `QuantityCalculatorCard` because it never renders without
 * a backend payload. This was the first `--gd-*` component to ship live on
 * an ungated surface. A boolean prop defaulting to legacy would have let
 * every test in this file quietly keep exercising the legacy path unless
 * each one remembered to pass the flag — green while testing the thing that
 * was never built. An import name makes that impossible: rendering the new
 * card requires writing its name.
 *
 * `MAX_CARDS`, `productDetailPath` and the entrance-motion variants are
 * shared by both — the per-turn card ceiling, the click-through route, and
 * the fade/stagger timing don't change with the visual language.
 */
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { trackProductCardClicked } from "@/features/marketplace/browse-analytics";
import {
  formatPriceFigure,
  formatPriceRange,
} from "@/features/marketplace/price-format";
import {
  STOCK_MARK,
  normalizeStockState,
  type KnownStockState,
} from "@/features/marketplace/stock-pill";
import { kgsLocaleForLanguage } from "@/lib/format-currency";
import { fmtSom, fmtSomRange } from "@/lib/format";
import type { ProductCardData } from "@/features/chat/useChatStream";

/**
 * Max cards shown per turn. Matches the API's own per-turn ceiling
 * (`_MAX_LIST_CARDS` in `garden/chat/recommend/pipeline.py`) so neither
 * rendering ever silently drops a product the answer talks about.
 *
 * Was 6, "to keep the bubble compact". That reasoning does not apply here —
 * the rail scrolls HORIZONTALLY, so extra cards cost no vertical space — and
 * it broke once a single turn could answer for several products: a ten-item
 * shopping list described eight products in the prose and rendered six
 * (reported 2026-08-13). Showing fewer cards than the text describes reads as
 * the assistant contradicting itself.
 */
const MAX_CARDS = 12;

/** The product-detail route a chat card links to. Shared by both renderings. */
export function productDetailPath(productId: string): string {
  return `/marketplace/products/${productId}`;
}

/** Entrance motion, shared by both renderings — identical stagger + fade. */
const railVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04 } },
};
const cardVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
};

// ============================================================================
// LEGACY — untouched. `--g-*` tokens, horizontal scroll-snap rail.
// ============================================================================

/**
 * One product, on the label grid: NAME / PRICE / SELLER, in that order, ruled
 * identically on every card so a rail of eight reads as one table rather than
 * eight little posters.
 *
 * What changed and why:
 *   • The price is the hero. It was 14px semibold body type sitting between the
 *     name and a stock pill, carrying no more weight than the seller address
 *     under it — on a surface whose entire job is "what does this cost". It is
 *     now the instrument face (`.g-data`, tabular, dotted zero) at display
 *     weight, with the unit set beside it in body type at meta weight so a
 *     column of figures aligns on one edge.
 *   • The unit is **сом**, not "KGS". `formatKgs` renders the ISO code, which
 *     is not what a buyer in Bishkek reads (PRODUCT.md → Terminology), and it
 *     disagreed with the marketplace and the seller sheet, which already said
 *     сом. Same `Intl` grouping, product's own spelling.
 *   • Stock is stated once, quietly, on the seller line — not as a coloured
 *     pill competing with the price. Still never colour alone: the dot carries
 *     a text label beside it.
 */
function CompactProductCard({
  card,
  position,
}: {
  card: ProductCardData;
  position: number;
}): ReactElement {
  const { t, i18n } = useTranslation("marketplace");
  const { t: tc } = useTranslation("chat");
  const locale = kgsLocaleForLanguage(i18n.language);
  const stockState = normalizeStockState(card.stock_state);
  const stockLabel = t(`stock.${stockState}`);
  const price = priceView(card, locale);
  const hasPrice = price.figure !== null;
  const priceFigure = price.figure;
  const unit = tc("products.currency");
  // The accessible name still carries the fused price — a screen reader should
  // hear "29 000 сом", not a figure and a unit as two unrelated fragments. A
  // BAND is spoken as a band ("от 5 500 до 14 000 сом") rather than as the two
  // figures either side of a dash, which a screen reader reads as a subtraction.
  const spokenPrice = !hasPrice
    ? t("card.noPrice")
    : price.isRange
      ? t("card.priceRangeA11y", {
          min: formatPriceFigure(price.low as number, locale),
          max: formatPriceFigure(price.high as number, locale),
        })
      : `${priceFigure} ${unit}`;

  return (
    <motion.div
      variants={cardVariants}
      className="w-[168px] flex-shrink-0 snap-start"
    >
      <Link
        to={productDetailPath(card.id)}
        onClick={() =>
          trackProductCardClicked(card.id, card.seller_id, position)
        }
        data-testid={`chat-product-card-${card.id}`}
        aria-label={`${card.name}, ${spokenPrice}, ${card.seller_business_name}, ${stockLabel}`}
        className="group/pcard flex h-full min-h-[44px] flex-col overflow-hidden rounded-[var(--g-radius)] border border-[var(--g-rule)] bg-[var(--g-surface)] text-foreground transition-[border-color,transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:border-[var(--g-primary)] hover:shadow-[var(--g-shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100"
      >
        {/* LANDSCAPE 4:3 — the house ratio, shared with the marketplace grid.
            A portrait frame was tried (the catalogue is 65% portrait, median
            w:h 0.749) and reverted: it matched the photographs but made the
            thread very tall. The rail below is what buys the room back, so
            the frame can stay in the shape the rest of the product uses.
            Cost, stated plainly: with `object-contain` a portrait photo now
            pillarboxes to about 56% of the slot width. Whole, but small. */}
        <div className="aspect-[4/3] w-full overflow-hidden bg-[var(--g-sunken)]">
          {/* Decorative: the name renders as text below and the link aria-label
              names the product, so an alt would be redundant. Hides on error so
              a missing/placeholder thumb never shows a broken-image glyph. */}
          <img
            src={card.photo_thumb_url}
            alt=""
            loading="lazy"
            width={168}
            height={126}
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
            // `object-contain`, not cover — the SCRUM-230 ruling, which fixed
            // the detail page and left every CARD still cropping. Cover fills
            // the frame and throws the overflow away: a 4:3 catalogue photo in
            // this wide slot loses roughly half its height, which reads as the
            // product being zoomed in. The sources are a mix of 16:9 phone
            // shots and square studio images, so no single cropping frame can
            // hold all of them; contain letterboxes against the slot's own
            // sunken surface and keeps the photograph whole.
            className="size-full object-contain transition-transform duration-300 group-hover/pcard:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover/pcard:scale-100"
          />
        </div>
        {/* The drawn rule between the image and the label block — the card's
            share of the slip's armature. */}
        <div className="flex flex-1 flex-col gap-2 border-t border-[var(--g-rule)] px-2.5 pb-2.5 pt-2">
          <h4 className="line-clamp-2 min-h-[2.2em] text-[13.5px] font-medium leading-[1.25] text-foreground">
            {card.name}
          </h4>

          <div className="mt-auto">
            <p className="flex items-baseline gap-1">
              {hasPrice ? (
                <>
                  {/* A band keeps the mono figure face but drops a step: two
                      grouped numbers and a dash at 15px overflow a 168px card. */}
                  <span
                    className={`g-data font-bold leading-none text-[var(--g-figure)] ${
                      price.isRange ? "text-[13.5px]" : "text-[16px]"
                    }`}
                  >
                    {priceFigure}
                  </span>
                  <span className="text-[11.5px] font-medium leading-none text-[var(--g-ink-2)]">
                    {unit}
                  </span>
                </>
              ) : (
                <span className="text-[13px] font-medium leading-none text-[var(--g-ink-2)]">
                  {t("card.noPrice")}
                </span>
              )}
            </p>
            {/* A LADDER («350-650-1250-1800») has real ends but pack-size rungs
                between them, so the band alone would imply a continuum the
                seller never offered. Said once, quietly, under the figure. */}
            {price.isTiered ? (
              <p className="mt-0.5 text-[10.5px] leading-tight text-[var(--g-ink-2)]">
                {t("card.priceTiered")}
              </p>
            ) : null}
          </div>

          <p className="flex min-w-0 items-center gap-1.5 text-[11.5px] leading-none">
            <span
              aria-hidden="true"
              className={
                "size-1.5 shrink-0 rounded-full " + STOCK_MARK[stockState].dot
              }
            />
            <span className={"shrink-0 " + STOCK_MARK[stockState].label}>
              {stockLabel}
            </span>
            <span aria-hidden="true" className="text-[var(--g-ink-3)]">
              ·
            </span>
            <span className="truncate text-[var(--g-ink-2)]">
              {card.seller_business_name}
            </span>
          </p>
        </div>
      </Link>
    </motion.div>
  );
}

export function ChatProductCards({
  cards,
}: {
  cards: ProductCardData[];
}): ReactElement | null {
  const { t } = useTranslation("chat");
  const prefersReduced = useReducedMotion() ?? false;
  if (cards.length === 0) return null;
  const shown = cards.slice(0, MAX_CARDS);

  return (
    <motion.div
      role="region"
      aria-label={t("products.title")}
      data-testid="chat-product-cards"
      className="mt-1 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1"
      variants={prefersReduced ? undefined : railVariants}
      initial={prefersReduced ? false : "hidden"}
      animate={prefersReduced ? undefined : "show"}
    >
      {shown.map((card, i) => (
        <CompactProductCard key={card.id} card={card} position={i} />
      ))}
    </motion.div>
  );
}

export default ChatProductCards;

// ============================================================================
// DESIGN WORLD — spec §4.6, `--gd-*` tokens, two-column grid.
// Gated by import name only (C1) — no call site renders this yet.
// ============================================================================

/**
 * A price cell that is present AND numeric, or `null`.
 *
 * Keeps the two guards the card already depended on, in one place so the band
 * fields cannot be read more loosely than `price_kgs` is:
 *
 * * `Number("")` is `0` and `0` is finite, so an empty or whitespace-only
 *   string would otherwise render a fabricated "0 сом" (the round-2 bug below);
 * * `"0"` / `"0.00"` are the backend honestly reporting a real zero price and
 *   must still render. ABSENCE and ZERO are different answers.
 */
function finitePrice(raw: string | number | null | undefined): number | null {
  if (raw == null) return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** What a card should actually show for its price. */
interface PriceView {
  /** The grouped figure(s) — `"5 500 – 14 000"` or `"5 500"`; null when unpriced. */
  figure: string | null;
  low: number | null;
  high: number | null;
  isRange: boolean;
  /** A vendor LADDER: real ends, but pack-size rungs between them. */
  isTiered: boolean;
}

/**
 * Resolve a card's price into what the buyer should see.
 *
 * Half the catalogue sells in a BAND. The card used to read `price_kgs` alone,
 * which for a banded product is its LOW end — so «5500-14000» was shown as a
 * firm "5 500 сом". A wrong price stated confidently is worse than the "price
 * on request" this feature is often mistaken for.
 *
 * `high > low` is required rather than assumed: a degenerate band (equal ends,
 * or a max below the min) is not a range, and rendering "5 500 – 5 500" would
 * be noise.
 */
function priceView(
  card: Pick<ProductCardData, "price_kgs" | "price_max_kgs" | "price_kind">,
  locale: Parameters<typeof formatPriceFigure>[1],
): PriceView {
  const low = finitePrice(card.price_kgs);
  const high = finitePrice(card.price_max_kgs);
  if (low === null) {
    return { figure: null, low: null, high: null, isRange: false, isTiered: false };
  }
  const isRange = high !== null && high > low;
  return {
    figure: isRange
      ? formatPriceRange(low, high as number, locale)
      : formatPriceFigure(low, locale),
    low,
    high: isRange ? high : null,
    isRange,
    isTiered: isRange && card.price_kind === "tiered",
  };
}

/** Narrow the live i18n language to the ru/ky/en `fmtSom` expects. Duplicated
 * locally — see EstimateCheckCard.tsx's `narrowLang`; no shared util exists
 * for this yet across the assistant-surface components. */
function narrowLang(raw: string): "ru" | "ky" | "en" {
  const base = raw.split("-")[0] ?? "ru";
  return base === "ky" || base === "en" ? base : "ru";
}

/**
 * The backend substitutes this exact sentinel whenever a product has no real
 * photo (`SEARCH_PLACEHOLDER_THUMB`, `apps/api/garden/chat/
 * product_search.py:456-464`) — so `photo_thumb_url` is never an empty
 * string on the wire in production (finding I1). `ProductDetailPage.tsx:74`
 * carries the identical literal for the same reason on the detail page.
 *
 * This is deliberately a THIRD copy of the literal rather than a shared
 * module: this fix round's staging is scoped to six files (this component,
 * its test, the three locale files, and `design-world.css`), none of which
 * is a shared constants module, and `stock-pill.ts` / `ProductDetailPage.tsx`
 * are outside that list. Centralising it is a one-line mechanical follow-up,
 * not a design decision — left for a task whose file list includes those
 * modules, and called out in the fix report rather than silently expanding
 * scope against an explicit staging instruction.
 */
const PRODUCT_PLACEHOLDER_IMG = "/placeholder/200x200.svg";

/**
 * Design (spec §4.6) draws only the `in_stock` example, in `--gd-success`.
 * `low` and `out_of_stock` are an attention register — not success, not an
 * outright error — so they take `--gd-accent-ink`, which this design world
 * already spends on exactly that register (spec §4.4, the "General advice"
 * verdict). Plain `--gd-ink-3` made them read as meta noise indistinguishable
 * from the location line above them (finding I4). `unknown` carries nothing
 * to draw attention to and keeps `--gd-ink-3`.
 */
const STOCK_COLOR: Record<KnownStockState, string> = {
  in_stock: "var(--gd-success)",
  low: "var(--gd-accent-ink)",
  out_of_stock: "var(--gd-accent-ink)",
  unknown: "var(--gd-ink-3)",
};

/**
 * "market · row · seller" (spec §4.6) — raw data, no labels. Empty segments
 * are dropped entirely rather than rendered blank, so a missing market or
 * pavilion never leaves a stray "· ·" or a leading/trailing separator.
 *
 * Every field here arrives through an unchecked cast (`raw as
 * ProductCardData`, `useChatStream.ts:651`) over the SSE frame or persisted
 * `cards_json` — neither is runtime-validated, and this app has no
 * ErrorBoundary anywhere. `String(s ?? "")` guards a genuinely-missing field
 * before `.trim()` runs; calling `.trim()` straight on `undefined` throws and
 * would have taken down the whole transcript (finding I3).
 */
function locationLine(card: ProductCardData): string {
  return [card.market_name_ru, card.pavilion_number, card.seller_business_name]
    .map((s) => String(s ?? "").trim())
    .filter((s) => s.length > 0)
    .join(" · ");
}

/**
 * The image slot's non-photographic placeholder. `ProductCardData` carries
 * no category field (verified against `useChatStream.ts`), so this is not
 * category artwork — two tempting wrong answers were rejected: keyword-
 * matching the product name to a category (this codebase has already shipped
 * a bug from exactly that — a query for "help" matching an HVLP sprayer),
 * and picking one category image as a "neutral" default (tile artwork on a
 * bag of primer is the same lie, just quieter). Instead: a `--gd-wash` panel
 * carrying the same 120×120 shyrdak diamond lattice the conversation ground
 * tiles (spec §4.3), at the photo's own 110px height and 10px radius — reads
 * as "no photo", not as a depiction of one. `category_slug` is backend
 * capability C7 for a real fix later.
 *
 * The pattern id is per-card so multiple placeholder-bearing cards in the
 * same grid never collide on one `<svg><pattern id>`.
 */
function PhotoPlaceholder({ patternId }: { patternId: string }): ReactElement {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0"
      style={{ backgroundColor: "var(--gd-wash)" }}
    >
      <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern
            id={patternId}
            width="120"
            height="120"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M60 4 L116 60 L60 116 L4 60 Z"
              fill="none"
              stroke="var(--gd-primary)"
              strokeOpacity={0.05}
              strokeWidth={1.5}
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
    </div>
  );
}

function ProductCardDesign({
  card,
  position,
  reserveContact,
}: {
  card: ProductCardData;
  position: number;
  /** Another card in this answer shows a contact row (see CardContactRow). */
  reserveContact: boolean;
}): ReactElement {
  const { t, i18n } = useTranslation("marketplace");
  const lang = narrowLang(i18n.language);
  const stockState = normalizeStockState(card.stock_state);
  const stockLabel = t(`stock.${stockState}`);

  // I2: `parseDecimal` silently returns 0 for anything non-numeric, so it
  // cannot distinguish "priced at 0" from "not a number" — the ambiguity
  // that let an unvalidated stream item render a confident "0 сом". Validate
  // with a raw `Number()` + `Number.isFinite` first (the same guard the
  // legacy card above already uses); only a genuinely finite number reaches
  // `fmtSom`, which needs no further parsing once that guard has passed.
  //
  // Round 2: `Number("")` is `0`, and `0` is finite — so an empty (or
  // whitespace-only) string slipped through this exact guard and still
  // rendered a fabricated "0 сом". The distinction that matters is ABSENCE
  // vs. ZERO: "0" / "0.00" are the backend honestly reporting a real price
  // of zero and must still render "0 сом"; "" / "   " carry no price at all
  // and must not. Reject the empty-string case before it ever reaches
  // `Number()`, so only "no value was sent" is treated as absence.
  // Both guards now live in `finitePrice` (see above), so `price_max_kgs` is
  // read exactly as strictly as `price_kgs` — a blank max must not become 0
  // and turn an exact price into a "5 500 – 0" band.
  const price = priceView(card, kgsLocaleForLanguage(i18n.language));
  const hasPrice = price.figure !== null;
  const priceText = !hasPrice
    ? t("card.noPrice")
    : price.isRange
      ? fmtSomRange(price.low as number, price.high as number, lang)
      : fmtSom(price.low as number, lang);

  const location = locationLine(card);
  // I3: same unvalidated-field guard as `locationLine` — trim only after
  // coercing a possibly-missing value to a string.
  const photoUrl = String(card.photo_thumb_url ?? "").trim();
  // I1: the backend's own "no photo" sentinel must be treated as absent, or
  // the honest placeholder below never executes outside a test.
  const hasPhoto = photoUrl !== "" && photoUrl !== PRODUCT_PLACEHOLDER_IMG;
  const patternId = `chat-product-lattice-${card.id}`;

  return (
    <motion.div
      variants={cardVariants}
      // A rail item, not a grid cell: a set width so the row scrolls instead
      // of reflowing, and a snap point so a swipe lands on a card edge rather
      // than halfway through one. The width is a third of the rail less its
      // two `gap-3` gaps, so a view holds three WHOLE cards (E1,
      // ui-review.md §4.8): at a fixed 260px the third card was cut mid-word
      // at the column's edge.
      // The card FRAME lives here now, not on the <Link>, because the contact
      // row has to sit inside the card and outside the link (see
      // CardContactRow). The lift-on-hover moved with it, so the whole card
      // still responds as one object.
      className="flex w-[calc((100%_-_1.5rem)/3)] flex-shrink-0 snap-start flex-col overflow-hidden rounded-[14px] transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
      style={{
        backgroundColor: "var(--gd-surface)",
        border: "1px solid var(--gd-rule-2)",
      }}
    >
      <Link
        to={productDetailPath(card.id)}
        onClick={() =>
          trackProductCardClicked(card.id, card.seller_id, position)
        }
        data-testid={`chat-product-card-${card.id}`}
        aria-label={`${card.name}, ${priceText}, ${card.seller_business_name}, ${stockLabel}`}
        className="flex min-h-[44px] flex-1 flex-col gap-[10px] p-4 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)] focus-visible:ring-inset active:scale-[0.99] motion-reduce:active:scale-100"
      >
        {/* Landscape 4:3, matching the compact card. Spec §4.3's fixed 110px
            strip is not restored: on a 260px rail card that is 2.4:1, and a
            4:3 photo in it loses half its height to letterboxing for no
            reason the design ever stated. 4:3 is the same landscape the
            marketplace grid uses. */}
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[10px] bg-[var(--gd-sunken,var(--g-sunken))]">
          <PhotoPlaceholder patternId={patternId} />
          {hasPhoto ? (
            <img
              src={photoUrl}
              alt=""
              loading="lazy"
              width={260}
              height={195}
              onError={(e) => {
                e.currentTarget.style.visibility = "hidden";
              }}
              // C2 (measured in Chromium): without `size-full`, Tailwind's
              // preflight (`img { max-width:100%; height:auto }`) beats the
              // `height` attribute, and an absolutely-positioned replaced
              // element with `left:0; right:0` AND a specified `width` drops
              // `right` — so a 312×110 slot rendered a 220×220 image, cropped
              // from the top with a placeholder band showing beside it.
              // `size-full` (the pattern the legacy card above already uses)
              // forces both axes to the slot regardless of the attributes.
              // See the compact card above: contain, not cover (SCRUM-230).
              // The 110px slot is design spec §4.3 and is left alone — the
              // crop was never the height, it was the fit.
              className="absolute inset-0 size-full object-contain"
            />
          ) : null}
        </div>

        <h4
          className="line-clamp-2 text-[16px] font-bold leading-snug"
          style={{ color: "var(--gd-ink)", fontFamily: "var(--gd-text)" }}
        >
          {card.name}
        </h4>

        <p
          data-testid="product-location"
          className="truncate text-[13.5px]"
          style={{ color: "var(--gd-ink-3)", fontFamily: "var(--gd-text)" }}
        >
          {location}
        </p>

        <p
          data-testid="product-stock"
          aria-label={t("aria.stockState", { state: stockLabel })}
          className="text-[13.5px] font-semibold"
          style={{
            color: STOCK_COLOR[stockState],
            fontFamily: "var(--gd-text)",
          }}
        >
          {stockLabel}
        </p>

        <p
          data-testid="product-price"
          className="mt-auto text-[17px] font-bold"
          style={{
            color: hasPrice ? "var(--gd-primary)" : "var(--gd-ink-3)",
            fontFamily: "var(--gd-text)",
          }}
        >
          {priceText}
        </p>
        {/* A LADDER has real ends but pack-size rungs between them; without
            this line the band implies a continuum the seller never offered. */}
        {price.isTiered ? (
          <p
            className="text-[12px] leading-tight"
            style={{ color: "var(--gd-ink-3)", fontFamily: "var(--gd-text)" }}
          >
            {t("card.priceTiered")}
          </p>
        ) : null}
      </Link>
      <CardContactRow card={card} reserveSpace={reserveContact} />
    </motion.div>
  );
}

/** The contact row's frame: a 1px rule over `py-2` around a 44px line (61px). */
const CONTACT_ROW_CLASS = "flex items-center gap-2 border-t px-4 py-2";

/** The numbers a card may show; a blank is absent, never an empty link. */
function contactOf(card: ProductCardData): { phone: string; whatsapp: string } {
  return {
    phone: (card.seller_phone_e164 ?? "").trim(),
    whatsapp: (card.seller_whatsapp_e164 ?? "").trim(),
  };
}

/** Whether a card renders a contact row at all. */
function hasContact(card: ProductCardData): boolean {
  const { phone, whatsapp } = contactOf(card);
  return phone !== "" || whatsapp !== "";
}

/**
 * The seller's contact, on the card — the thing buyers were asking the
 * assistant for and it could not answer.
 *
 * It sits OUTSIDE the card's `<Link>` on purpose. The whole card links to the
 * product, and a `tel:` anchor nested inside another anchor is invalid HTML:
 * browsers recover by closing the outer link early, which silently breaks the
 * card's own click target. So the card's frame moved up to the wrapper and the
 * link now covers only the product content.
 *
 * NEITHER NUMBER MEANS NO ROW. The server resolves what may be shown
 * (`garden.marketplace.contact`): 9 of 79 QA sellers carry a sentinel phone
 * minted to clear the unique phone index, and 6 of those have no WhatsApp
 * either. Those cards render no control here — not a disabled button, which
 * still promises a number that does not exist. The market and pavilion above
 * stay, so the buyer can still walk to the stall.
 *
 * They keep the row's SPACE when a neighbour has a row (E2, ui-review.md
 * §4.8). The price is `mt-auto` inside the link above, so a card with no row
 * printed its price 61px lower than its neighbours'. An empty row of the same
 * anatomy, hidden from assistive tech and holding nothing, puts every price on
 * one line. With no neighbour that has a number there is nothing to line up
 * with, so no space is kept (fix round 1, M2): a reserve there would only end
 * every card in a blank 61px band.
 */
function CardContactRow({
  card,
  reserveSpace,
}: {
  card: ProductCardData;
  /** Keep the row's height when this card has no number (a neighbour has one). */
  reserveSpace: boolean;
}): ReactElement | null {
  const { t } = useTranslation("marketplace");
  const { phone, whatsapp } = contactOf(card);
  if (!phone && !whatsapp) {
    if (!reserveSpace) return null;
    return (
      <div
        aria-hidden="true"
        data-testid="card-contact-placeholder"
        className={`${CONTACT_ROW_CLASS} border-transparent`}
      >
        {/* The 44px line a dial link or the WhatsApp button would fill. */}
        <span className="h-11" />
      </div>
    );
  }

  const seller = card.seller_business_name;
  return (
    <div
      data-testid="card-contact"
      className={CONTACT_ROW_CLASS}
      style={{ borderColor: "var(--gd-rule-2)" }}
    >
      {phone ? (
        <a
          href={`tel:${phone}`}
          data-testid="card-contact-call"
          aria-label={t("card.callAria", { seller, phone })}
          // The number IS the link — readable as text and tappable, so a
          // desktop reader can copy it and a phone dials it.
          className="g-data min-h-[44px] flex-1 truncate py-3 text-[14px] font-semibold leading-none underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)]"
          style={{ color: "var(--gd-primary)" }}
        >
          {phone}
        </a>
      ) : (
        <span
          className="flex-1 truncate text-[13px]"
          style={{ color: "var(--gd-ink-3)", fontFamily: "var(--gd-text)" }}
        >
          {t("card.whatsapp")}
        </span>
      )}
      {whatsapp ? (
        <a
          // wa.me wants digits only — a leading "+" produces a dead link.
          href={`https://wa.me/${whatsapp.replace(/[^\d]/g, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="card-contact-whatsapp"
          aria-label={t("card.whatsappAria", { seller })}
          className="grid size-11 shrink-0 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)]"
          style={{ backgroundColor: "var(--gd-sunken,var(--g-sunken))" }}
        >
          {/* Decorative: the link carries its own accessible name. */}
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="size-5"
            fill="currentColor"
            style={{ color: "var(--gd-primary)" }}
          >
            <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.02h-.01a8.23 8.23 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.13-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.87.85-.87 2.07 0 1.22.89 2.4 1.02 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.22-.16-.47-.28Z" />
          </svg>
        </a>
      ) : null}
    </div>
  );
}

export function ChatProductCardsDesign({
  cards,
}: {
  cards: ProductCardData[];
}): ReactElement | null {
  const { t } = useTranslation("chat");
  const prefersReduced = useReducedMotion() ?? false;
  if (cards.length === 0) return null;
  const shown = cards.slice(0, MAX_CARDS);
  // A card without a number keeps the contact row's height only when another
  // card here shows one, so the prices share a line (CardContactRow).
  const reserveContact = shown.some(hasContact);

  return (
    <motion.div
      role="region"
      aria-label={t("products.title")}
      data-testid="chat-product-cards"
      // A horizontal RAIL, not a stacked grid. Two columns of tall cards
      // pushed the rest of the conversation off screen, and results are a
      // side-by-side comparison — scanning across beats scrolling down.
      // Same pattern the compact rail above already uses, so the two
      // surfaces behave identically under a swipe.
      // `scroll-px-1` keeps a snapped card off the container's own edge, so the
      // first card does not sit flush against the thread margin after a swipe.
      // `overflow-x-auto` CONTAINS the scroll: the rail scrolls, the page must
      // not — the page-level horizontal-overflow rule still applies around it.
      // With a fourth card waiting past the edge, the rail fades out on the
      // right so it reads as "more": macOS shows no scrollbar to say so (E1).
      // 12px, not a share of the rail (fix round 1, I2): three cards fill the
      // rail exactly, so the third card's edge IS the rail's edge, and its
      // content starts 17px in (1px border + `p-4`). An 8% fade (~59px) ate
      // the card's image and title; 12px softens only its border and padding.
      className={
        "mt-1 flex snap-x snap-mandatory scroll-px-1 gap-3 overflow-x-auto pb-1" +
        (shown.length > 3
          ? " [mask-image:linear-gradient(to_right,black_calc(100%_-_12px),transparent)]"
          : "")
      }
      variants={prefersReduced ? undefined : railVariants}
      initial={prefersReduced ? false : "hidden"}
      animate={prefersReduced ? undefined : "show"}
    >
      {shown.map((card, i) => (
        <ProductCardDesign
          key={card.id}
          card={card}
          position={i}
          reserveContact={reserveContact}
        />
      ))}
    </motion.div>
  );
}
